import React from "react";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useAuth } from "../contexts/auth-provider";
import routesForPublic from "../utils/public-routes";
import routesForAuthenticatedOnly from "../utils/authenticated-routes";
import routesForNotAuthenticatedOnly from "../utils/nonAuthenticated-routes";
import PreviousLandingPage from "../pages/landing-page/previous-landing-page";
import { AccessDenied } from "src/pages/AccessDenied";
import { DefaultLayout } from "../layout/DefaultLayout";
import { roleBasesRoute } from "src/utils/roleBasesRoute";
import { FEATURE_NAMES } from "src/utils/isAllowedFeature";
import { restaurantSegment } from "src/utils/restaurantUrl";

const AuthHydrationLoader = () => (
  <div
    className="flex flex-col items-center justify-center min-h-screen bg-[#f8fafc] dark:bg-boxdark-2"
    role="status"
    aria-label="Loading"
  >
    <div className="w-10 h-10 border-2 border-[#111827] dark:border-white border-t-transparent rounded-full animate-spin" />
    <p className="mt-4 text-sm text-[#6b7280] dark:text-bodydark">Loading...</p>
  </div>
);

const FallbackRoute = () => {
  const { token, user, userRole, hasRestaurant, permissions } = useAuth();
  const isAuthorized =
    !!token && userRole && Object.keys(user || {}).length > 0;
  const location = useLocation();

  if (!isAuthorized && location.pathname === "/login") {
    return <Navigate to="/login" replace />;
  }
  if (
    isAuthorized &&
    (location.pathname === "/login" || location.pathname === "/")
  ) {
    return (
      <Navigate
        to={
          hasRestaurant?.correlationId &&
          permissions[FEATURE_NAMES.ReportingAndAnalytics]
            ? `restaurants/${restaurantSegment(hasRestaurant.correlationId, hasRestaurant?.name)}/reports/overview`
            : hasRestaurant?.correlationId
            ? `restaurants/${restaurantSegment(hasRestaurant.correlationId, hasRestaurant?.name)}/restaurant-setting`
            : "/my-restaurants"
        }
        replace
      />
    );
  }
  return <Navigate to="/login" replace />;
};

const Routes: React.FC = () => {
  const { token, user, userRole, permissions, hasRestaurant, authHydrated } =
    useAuth();
  const isAuthorized =
    !!token && userRole && Object.keys(user || {}).length > 0;
  const publicRoute = [
    ...routesForPublic,
    {
      path: "/",
      element:
        isAuthorized ? (
          <DefaultLayout />
        ) : (
          <div className="h-full min-h-0 overflow-y-auto overflow-x-hidden">
            <PreviousLandingPage />
          </div>
        ),
      children: [
        {
          path: "/",
          element:
            isAuthorized ? (
              <Navigate
                to={
                  hasRestaurant?.correlationId &&
                  permissions[FEATURE_NAMES.ReportingAndAnalytics]
                    ? `restaurants/${restaurantSegment(hasRestaurant.correlationId, hasRestaurant?.name)}/reports/overview`
                    : hasRestaurant?.correlationId
                    ? `restaurants/${restaurantSegment(hasRestaurant.correlationId, hasRestaurant?.name)}/restaurant-setting`
                    : "/my-restaurants"
                }
                replace
              />
            ) : (
              <div className="h-full min-h-0 overflow-y-auto overflow-x-hidden">
                <PreviousLandingPage />
              </div>
            ),
        },
      ],
    },
  ];

  // Combine and conditionally include routes based on authentication status
  const routes = [
    ...publicRoute,
    {
      path: "/access-denied",
      element: <AccessDenied />,
    },
    ...(isAuthorized
      ? roleBasesRoute(routesForAuthenticatedOnly, permissions)
      : routesForNotAuthenticatedOnly),
  ];

  // Don't render router until auth state has been restored from storage
  if (!authHydrated) {
    return <AuthHydrationLoader />;
  }

  // Create router with routes
  const router = createBrowserRouter([
    ...routes,
    {
      path: "*",
      element: <FallbackRoute />,
    },
  ]);

  return <RouterProvider router={router} />;
};

export default Routes;
